// frontend/src/context/SavedContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { savedApi } from '../api/saved';
import { useAuth } from './AuthContext';

const SavedContext = createContext();

export const useSaved = () => useContext(SavedContext);

export const SavedProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [savedIds, setSavedIds] = useState(new Set()); // Using a Set for fast lookups
  const [loading] = useState(false);

  // Load saved items when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      fetchSavedIds();
    } else {
      setSavedIds(new Set());
    }
  }, [isAuthenticated]);

  const fetchSavedIds = async () => {
    try {
      const items = await savedApi.getSavedItems();
      // Extract just the IDs for quick checking
      const ids = new Set(items.map(item => item.item_id));
      setSavedIds(ids);
    } catch (err) {
      console.error("Failed to fetch saved items:", err);
    }
  };

  const isSaved = (itemId) => {
    return savedIds.has(itemId);
  };

  const toggleSave = async (itemId, itemType = 'vehicle') => {
    if (!isAuthenticated) {
      alert("Please log in to save items.");
      return false;
    }

    // Optimistic UI Update (Update immediately before server responds)
    const wasSaved = savedIds.has(itemId);
    setSavedIds(prev => {
      const newSet = new Set(prev);
      if (wasSaved) newSet.delete(itemId);
      else newSet.add(itemId);
      return newSet;
    });

    try {
      await savedApi.toggleSaved(itemId, itemType);
      return !wasSaved; // Return new state
    } catch (err) {
      console.error("Failed to toggle save:", err);
      // Revert on error
      fetchSavedIds();
      return wasSaved;
    }
  };

  const value = {
    savedIds,
    isSaved,
    toggleSave,
    refreshSaved: fetchSavedIds,
    loading
  };

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
};